import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Bookmark, BookmarkCheck, CheckCircle2, CalendarClock, Wallet, FileText, ListChecks, ExternalLink,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { findScholarship } from "@/data/scholarships";
import { benefitCoverage, benefitCategories } from "@/data/roadmap";
import { computeMatch, daysUntil } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { EligibilityBadge, StatusBadge, SourceBadge, Disclaimer } from "@/components/common";

export default function ScholarshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, updateData } = useApp();
  const s = findScholarship(id);

  if (!s) return <div className="p-8 text-center text-slate-500">Beasiswa tidak ditemukan.</div>;

  const match = computeMatch(data.profile, s);
  const d = daysUntil(s.deadline);
  const saved = data.savedScholarships.includes(s.id);
  const coverage = benefitCoverage[s.id] || {};

  const toggleSave = () => {
    updateData((prev) => {
      const isSaved = prev.savedScholarships.includes(s.id);
      toast[isSaved ? "message" : "success"](isSaved ? "Dihapus dari tersimpan" : "Beasiswa disimpan");
      return { ...prev, savedScholarships: isSaved ? prev.savedScholarships.filter((x) => x !== s.id) : [...prev.savedScholarships, s.id] };
    });
  };

  const gaps = match.dims.filter((x) => x.state === "FAIL" || x.state === "UNKNOWN");

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-[#5B21B6]"><ArrowLeft className="h-4 w-4" /> Kembali</button>

      {/* Header */}
      <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">{s.name}</h1>
              <StatusBadge status={s.status} />
            </div>
            <p className="mt-1 text-slate-500">{s.provider} · {s.country}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-[#6D28D9]">{s.degree}</span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-600">{s.funding}</span>
              <span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-[#6D28D9]">Min IELTS {s.language.min}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] px-5 py-3 text-center text-white">
              <div className="font-display text-2xl font-extrabold">{match.matchScore}%</div>
              <div className="text-[11px] text-purple-100">Match</div>
            </div>
            <Button onClick={toggleSave} variant="outline" data-testid="detail-save" className="rounded-full border-purple-200 text-[#5B21B6]">
              {saved ? <BookmarkCheck className="mr-1 h-4 w-4" /> : <Bookmark className="mr-1 h-4 w-4" />} {saved ? "Tersimpan" : "Simpan"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Overview */}
          <Section title="Overview"><p className="text-sm text-slate-600">{s.overview}</p></Section>

          {/* Match Analysis */}
          <Section title="Match Analysis" icon={ListChecks}>
            <p className="mb-3 text-xs text-slate-400">Setiap kriteria dinilai berdasarkan bukti dari profilmu. UNKNOWN tidak pernah menjadi PASS.</p>
            <div className="space-y-2">
              {match.dims.map((dim) => (
                <div key={dim.key} className="flex items-start justify-between gap-3 rounded-xl border border-purple-50 bg-[#FBFAFF] p-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{dim.label}</p>
                    <p className="text-xs text-slate-500">{dim.detail}</p>
                  </div>
                  <EligibilityBadge state={dim.state} testid={`elig-${dim.key}`} />
                </div>
              ))}
            </div>
          </Section>

          {/* Potential gaps */}
          <Section title="Potensi Gap & Next Actions">
            {gaps.length === 0 ? (
              <p className="text-sm text-emerald-600">Tidak ada gap besar yang terdeteksi dari data profilmu. Tetap verifikasi syarat resmi.</p>
            ) : (
              <ul className="space-y-2">
                {gaps.map((g) => (
                  <li key={g.key} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-0.5"><EligibilityBadge state={g.state} /></span>
                    <span>{g.label}: {g.state === "UNKNOWN" ? "Kami belum bisa memastikan ini, verifikasi ke penyelenggara." : g.detail}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4"><Link to="/app/roadmap"><Button variant="outline" className="rounded-full border-purple-200 text-[#5B21B6]">Buka Roadmap untuk menutup gap →</Button></Link></div>
          </Section>

          {/* Documents */}
          <Section title="Required Documents" icon={FileText}>
            <ul className="grid gap-2 sm:grid-cols-2">
              {s.documents.map((doc) => <li key={doc} className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-[#A78BFA]" /> {doc}</li>)}
            </ul>
          </Section>
        </div>

        <div className="space-y-6">
          {/* Timeline & funding */}
          <Section title="Timeline" icon={CalendarClock}>
            <p className="text-sm text-slate-600">{s.deadline ? <>Deadline: <b>{s.deadline}</b> {d > 0 ? `(${d} hari lagi)` : "(sudah lewat)"}</> : "Deadline belum diverifikasi untuk siklus ini."}</p>
            <p className="mt-1 text-xs text-slate-400">Siklus: {s.cycle}</p>
          </Section>

          <Section title="Funding & Benefit" icon={Wallet}>
            <div className="space-y-1.5">
              {Object.entries(coverage).map(([bid, c]) => {
                const cat = benefitCategories.find((x) => x.id === bid);
                return (
                  <div key={bid} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{cat?.label || bid}</span>
                    <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-[#6D28D9]">{c.status}</span>
                  </div>
                );
              })}
            </div>
            <Link to="/app/benefit" className="mt-3 inline-block text-sm font-semibold text-[#7C3AED] hover:underline">Detail benefit →</Link>
          </Section>

          <SourceBadge scholarship={s} />
          <a href={s.source_url} target="_blank" rel="noopener noreferrer">
            <Button className="w-full rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]"><ExternalLink className="mr-1.5 h-4 w-4" /> Sumber Resmi</Button>
          </a>
        </div>
      </div>

      <Disclaimer>Skor match adalah diagnostik kecocokan profil, bukan probabilitas penerimaan. Selalu verifikasi syarat, deadline, dan pendanaan langsung dari penyelenggara resmi.</Disclaimer>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
      <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold text-slate-900">{Icon && <Icon className="h-4 w-4 text-[#7C3AED]" />} {title}</h2>
      {children}
    </div>
  );
}
